// Delta compression system for efficient snapshot storage
import { ApplicationSnapshot } from '../types/applicationState';
import { deepClone } from './clone';
import { log } from './logger';

interface CompressedSnapshotBase {
  id: string;
  timestamp: number;
  compressionRatio: number;
  originalSize: number;
  compressedSize: number;
}

export interface FullCompressedSnapshot extends CompressedSnapshotBase {
  type: 'full';
  data: ApplicationSnapshot;
}

export interface DeltaCompressedSnapshot extends CompressedSnapshotBase {
  type: 'delta';
  data: DeltaChange[];
  baseSnapshotId: string;
}

export type CompressedSnapshot = FullCompressedSnapshot | DeltaCompressedSnapshot;

export interface DeltaChange {
  path: string[];
  type: 'add' | 'remove' | 'update';
  oldValue?: unknown;
  newValue?: unknown;
}

export class SnapshotCompressor {
  private fullSnapshots: Map<string, CompressedSnapshot> = new Map();
  private deltaSnapshots: Map<string, CompressedSnapshot> = new Map();
  private releasedFullSnapshots = new Set<string>();
  private compressionThreshold = 0.7; // Only compress if we save >30%

  /**
   * Compress a snapshot by comparing it to the previous one
   */
  compress(
    snapshotId: string,
    snapshot: ApplicationSnapshot,
    previousSnapshot?: ApplicationSnapshot
  ): CompressedSnapshot {
    const originalData = JSON.stringify(snapshot);
    const originalSize = new Blob([originalData]).size;

    // If no previous snapshot or it's been a while, create a full snapshot
    if (!previousSnapshot || this.shouldCreateFullSnapshot(snapshot, previousSnapshot)) {
      return this.createFullSnapshot(snapshotId, snapshot, originalSize);
    }

    const baseSnapshotId = this.findBaseSnapshotId(previousSnapshot);
    const baseSnapshot = baseSnapshotId ? this.getFullSnapshot(baseSnapshotId) : null;
    if (!baseSnapshot || !baseSnapshotId) {
      return this.createFullSnapshot(snapshotId, snapshot, originalSize);
    }

    // Each delta is independently applicable to its named full base. This avoids
    // silently dropping earlier edits when a caller passes the prior delta state.
    const delta = this.createDelta(baseSnapshot, snapshot);
    const deltaData = JSON.stringify(delta);
    const deltaSize = new Blob([deltaData]).size;
    const compressionRatio = 1 - (deltaSize / originalSize);

    // If compression isn't effective enough, create a full snapshot
    if (compressionRatio < this.compressionThreshold) {
      return this.createFullSnapshot(snapshotId, snapshot, originalSize);
    }

    // Create compressed delta snapshot
    const compressed: DeltaCompressedSnapshot = {
      id: snapshotId,
      timestamp: Date.now(),
      type: 'delta',
      data: delta,
      baseSnapshotId,
      compressionRatio,
      originalSize,
      compressedSize: deltaSize
    };

    this.deltaSnapshots.set(snapshotId, compressed);
    return compressed;
  }

  /**
   * Decompress a snapshot back to full ApplicationSnapshot
   */
  decompress(compressed: CompressedSnapshot): ApplicationSnapshot | null {
    if (compressed.type === 'full') {
      return this.deepClone(compressed.data);
    }

    // For delta snapshots, we need to reconstruct from base + deltas
    if (!compressed.baseSnapshotId) {
      log.error('SnapshotCompressor', 'Delta snapshot missing base snapshot ID');
      return null;
    }

    const baseSnapshot = this.getFullSnapshot(compressed.baseSnapshotId);
    if (!baseSnapshot) {
      log.error('SnapshotCompressor', 'Base snapshot not found:', compressed.baseSnapshotId);
      return null;
    }

    return this.applyDelta(baseSnapshot, compressed.data);
  }

  /**
   * Create a full snapshot
   */
  private createFullSnapshot(
    snapshotId: string,
    snapshot: ApplicationSnapshot,
    originalSize: number
  ): CompressedSnapshot {
    const compressed: FullCompressedSnapshot = {
      id: snapshotId,
      timestamp: Date.now(),
      type: 'full',
      data: this.deepClone(snapshot),
      compressionRatio: 0,
      originalSize,
      compressedSize: originalSize
    };

    this.fullSnapshots.set(snapshotId, compressed);
    return compressed;
  }

  /**
   * Create delta between two snapshots
   */
  private createDelta(oldSnapshot: ApplicationSnapshot, newSnapshot: ApplicationSnapshot): DeltaChange[] {
    const changes: DeltaChange[] = [];
    this.compareObjects(oldSnapshot, newSnapshot, [], changes);
    return changes;
  }

  /**
   * Compare two objects and generate delta changes
   */
  private compareObjects(
    oldObj: unknown,
    newObj: unknown,
    path: string[],
    changes: DeltaChange[]
  ): void {
    // Handle different types
    if (oldObj === newObj) return;

    if (typeof oldObj !== typeof newObj || oldObj === null || newObj === null) {
      changes.push({
        path: [...path],
        type: 'update',
        oldValue: oldObj,
        newValue: newObj
      });
      return;
    }

    if (Array.isArray(oldObj) && Array.isArray(newObj)) {
      this.compareArrays(oldObj, newObj, path, changes);
      return;
    }

    if (typeof oldObj === 'object' && typeof newObj === 'object') {
      const oldRecord = oldObj as Record<string, unknown>;
      const newRecord = newObj as Record<string, unknown>;
      const allKeys = new Set([...Object.keys(oldRecord), ...Object.keys(newRecord)]);

      for (const key of allKeys) {
        const newPath = [...path, key];

        if (!(key in oldRecord)) {
          changes.push({
            path: newPath,
            type: 'add',
            newValue: newRecord[key]
          });
        } else if (!(key in newRecord)) {
          changes.push({
            path: newPath,
            type: 'remove',
            oldValue: oldRecord[key]
          });
        } else {
          this.compareObjects(oldRecord[key], newRecord[key], newPath, changes);
        }
      }
      return;
    }

    // Primitive values that are different
    changes.push({
      path: [...path],
      type: 'update',
      oldValue: oldObj,
      newValue: newObj
    });
  }

  /**
   * Compare arrays and generate delta changes
   */
  private compareArrays(
    oldArray: unknown[],
    newArray: unknown[],
    path: string[],
    changes: DeltaChange[]
  ): void {
    const maxLength = Math.max(oldArray.length, newArray.length);
    
    for (let i = 0; i < maxLength; i++) {
      const newPath = [...path, i.toString()];
      
      if (i >= oldArray.length) {
        changes.push({
          path: newPath,
          type: 'add',
          newValue: newArray[i]
        });
      } else if (i >= newArray.length) {
        changes.push({
          path: newPath,
          type: 'remove',
          oldValue: oldArray[i]
        });
      } else {
        this.compareObjects(oldArray[i], newArray[i], newPath, changes);
      }
    }
  }

  /**
   * Apply delta changes to a base snapshot
   */
  private applyDelta(baseSnapshot: ApplicationSnapshot, delta: DeltaChange[]): ApplicationSnapshot {
    const result = this.deepClone(baseSnapshot);
    const removals = delta
      .filter((change) => change.type === 'remove')
      .sort((left, right) => {
        const leftParent = left.path.slice(0, -1).join('\u0000');
        const rightParent = right.path.slice(0, -1).join('\u0000');
        if (leftParent !== rightParent) return leftParent.localeCompare(rightParent);
        return Number(right.path[right.path.length - 1]) - Number(left.path[left.path.length - 1]);
      });

    for (const change of [...delta.filter((change) => change.type !== 'remove'), ...removals]) {
      this.applyChange(result, change);
    }
    
    return result;
  }

  /**
   * Apply a single delta change to an object
   */
  private applyChange(obj: object, change: DeltaChange): void {
    const { path, type, newValue } = change;

    if (path.length === 0) {
      log.error('SnapshotCompressor', 'Cannot apply change with empty path');
      return;
    }

    // Navigate to the parent object
    let current: object = obj;
    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
      const propertyKey = Array.isArray(current) ? Number(key) : key;
      const currentValue = Reflect.get(current, propertyKey);
      if (!currentValue || typeof currentValue !== 'object') {
        Reflect.set(current, propertyKey, {});
      }
      const next = Reflect.get(current, propertyKey);
      if (!next || typeof next !== 'object') return;
      current = next;
    }

    const finalKey = path[path.length - 1];

    switch (type) {
      case 'add':
      case 'update':
        Reflect.set(current, Array.isArray(current) ? Number(finalKey) : finalKey, this.deepClone(newValue));
        break;
      case 'remove':
        if (Array.isArray(current)) {
          const index = Number(finalKey);
          if (Number.isInteger(index)) current.splice(index, 1);
        } else {
          Reflect.deleteProperty(current, finalKey);
        }
        break;
    }
  }

  /**
   * Determine if we should create a full snapshot instead of delta
   */
  private shouldCreateFullSnapshot(
    newSnapshot: ApplicationSnapshot,
    previousSnapshot: ApplicationSnapshot
  ): boolean {
    // Create full snapshot every 10 deltas to avoid long delta chains
    const baseSnapshotId = this.findBaseSnapshotId(previousSnapshot);
    const deltaCount = baseSnapshotId
      ? Array.from(this.deltaSnapshots.values()).filter((snapshot): snapshot is DeltaCompressedSnapshot =>
        snapshot.type === 'delta' && snapshot.baseSnapshotId === baseSnapshotId
      ).length
      : 0;
    if (deltaCount >= 10) return true;

    // If the change is too large, create a full snapshot
    const changeSize = this.estimateChangeSize(previousSnapshot, newSnapshot);
    const totalSize = JSON.stringify(newSnapshot).length;
    
    return (changeSize / totalSize) > 0.5; // If >50% changed, use full snapshot
  }

  /**
   * Estimate the size of changes between snapshots
   */
  private estimateChangeSize(oldSnapshot: ApplicationSnapshot, newSnapshot: ApplicationSnapshot): number {
    const oldStr = JSON.stringify(oldSnapshot);
    const newStr = JSON.stringify(newSnapshot);
    
    // Simple estimation: count differing characters
    let differences = 0;
    const maxLength = Math.max(oldStr.length, newStr.length);
    
    for (let i = 0; i < maxLength; i++) {
      if (oldStr[i] !== newStr[i]) {
        differences++;
      }
    }
    
    return differences;
  }

  /**
   * Find the base snapshot ID for a given snapshot
   */
  private findBaseSnapshotId(_snapshot: ApplicationSnapshot): string | undefined {
    // For now, we'll use a simple approach: find the most recent full snapshot
    const fullSnapshots = Array.from(this.fullSnapshots.values())
      .sort((a, b) => b.timestamp - a.timestamp);
    
    return fullSnapshots.length > 0 ? fullSnapshots[0].id : undefined;
  }

  /**
   * Get a full snapshot by ID
   */
  private getFullSnapshot(id: string): ApplicationSnapshot | null {
    const compressed = this.fullSnapshots.get(id);
    return compressed && compressed.type === 'full' ? compressed.data : null;
  }

  /**
   * Deep clone an object
   */
  private deepClone<T>(obj: T): T {
    return deepClone(obj);
  }

  /**
   * Get compression statistics
   */
  getCompressionStats(): {
    totalSnapshots: number;
    fullSnapshots: number;
    deltaSnapshots: number;
    averageCompressionRatio: number;
    totalSaved: number;
  } {
    const allSnapshots = [
      ...Array.from(this.fullSnapshots.values()),
      ...Array.from(this.deltaSnapshots.values())
    ];

    const totalSnapshots = allSnapshots.length;
    const fullSnapshots = this.fullSnapshots.size;
    const deltaSnapshots = this.deltaSnapshots.size;

    const totalCompressionRatio = allSnapshots.reduce((sum, s) => sum + s.compressionRatio, 0);
    const averageCompressionRatio = totalSnapshots > 0 ? totalCompressionRatio / totalSnapshots : 0;

    const totalOriginalSize = allSnapshots.reduce((sum, s) => sum + s.originalSize, 0);
    const totalCompressedSize = allSnapshots.reduce((sum, s) => sum + s.compressedSize, 0);
    const totalSaved = totalOriginalSize - totalCompressedSize;

    return {
      totalSnapshots,
      fullSnapshots,
      deltaSnapshots,
      averageCompressionRatio,
      totalSaved
    };
  }

  /** Release one owner's entry, retaining full bases until their last delta is released. */
  release(snapshotId: string): void {
    this.deltaSnapshots.delete(snapshotId);
    if (this.fullSnapshots.has(snapshotId)) this.releasedFullSnapshots.add(snapshotId);

    const requiredBases = new Set(
      Array.from(this.deltaSnapshots.values()).flatMap(snapshot =>
        snapshot.type === 'delta' ? [snapshot.baseSnapshotId] : []
      )
    );
    for (const id of this.releasedFullSnapshots) {
      if (!requiredBases.has(id)) {
        this.fullSnapshots.delete(id);
        this.releasedFullSnapshots.delete(id);
      }
    }
  }

  /**
   * Clear all stored snapshots
   */
  clear(): void {
    this.fullSnapshots.clear();
    this.deltaSnapshots.clear();
    this.releasedFullSnapshots.clear();
  }
}

// Global compressor instance
export const snapshotCompressor = new SnapshotCompressor();
